/**
 * QuestionView — диспетчер по типу вопроса (Фаза 2).
 *
 * Общая оболочка: прогресс «Вопрос X из N», бейдж типа (KIND_LABEL) и серверный
 * обратный отсчёт (через useCountdown). Тело вопроса рендерится подходящим
 * под-view по `kind`: известные типы — явные case (ChoiceView / PollView /
 * OpenView / MatchView / OrderView / WordView / MultiView), default (отсутствие
 * kind — legacy choice) тоже choice; НЕзнакомый kind ('unknown', Фаза 14 §4
 * спеки) — UnknownKindView («Обновите клиент», read-only).
 *
 * `variant="answer"` (игрок): интерактивный ввод; клик/сабмит → onAnswer с
 * type-специфичным PlayerAnswer. `variant="spectate"` (ведущий): только
 * просмотр + живая статистика. Таймер — клиентский для UX; завершение не
 * инициируем — сервер сам пришлёт reveal/stats (realtime-protocol.md §5).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountdown } from '../hooks';
import {
  questionKind,
  type PlayerAnswer,
  type PlayerPos,
  type QuestionPayload,
  type RevealPayload,
  type RunDeadPayload,
  type WordCountEntry,
} from '../protocol';
import { playTick } from '../sound';
import { ChoiceView } from './views/ChoiceView';
import { PollView } from './views/PollView';
import { OpenView } from './views/OpenView';
import { MatchView } from './views/MatchView';
import { OrderView } from './views/OrderView';
import { WordView } from './views/WordView';
import { MultiView } from './views/MultiView';
import { RunView } from './views/RunView';
import { RunHostView } from './views/RunHostView';
import { UnknownKindView } from './views/UnknownKindView';
import type { QuestionBodyProps } from './views/types';
import { FormulaText } from './FormulaText';
import { CodeBlock } from './CodeBlock';

export interface QuestionViewProps {
  question: QuestionPayload;
  variant?: 'answer' | 'spectate';
  /** Игрок: обработчик ответа; под-view вызывает с type-специфичным PlayerAnswer. */
  onAnswer?: (answer: PlayerAnswer) => void;
  /**
   * Run-вопрос (Фаза 14, эпик B): отправка позиции игрока (~3 Гц, только при
   * изменении; экран игрока превращает в WS pos). Прокидывается в RunView.
   */
  onPos?: (x: number, y: number) => void;
  /**
   * Run-вопрос: последний пакет позиций игроков (positions, только ведущему);
   * RunHostView рисует доты и счётчики углов.
   */
  positions?: PlayerPos[] | null;
  /**
   * Run-вопрос (0.14.3, S1/R2): серверное Unix-мс последнего пакета positions
   * (PositionsPayload.t) — база интерполяции дотов ведущего.
   */
  positionsT?: number | null;
  /**
   * Run-вопрос (0.14.3, S4): персональный dead-пуш run_dead (сервер зафиксировал
   * гибель) — RunView мгновенно взрывает, идемпотентно локальному детекту.
   */
  runDead?: RunDeadPayload | null;
  /** Ник игрока (подпись человечка RunView, §3.4). */
  playerNick?: string;
  /** Игрок: уже данный ответ (для блокировки после отправки). */
  myAnswer?: PlayerAnswer | null;
  /** Раскрытие правильного ответа (событие reveal); null, пока не пришло. */
  reveal?: RevealPayload | null;
  /** Живая статистика counts[i] (только choice/poll); null для open/match/order. */
  counts?: number[] | null;
  /** Живое облако word-вопроса из stats.words (И8); null для прочих типов. */
  liveWords?: WordCountEntry[] | null;
  /** Число участников (ведущий) — для сводки «N из M» при отсутствии counts. */
  participants?: number;
  /**
   * Заморозка отсчёта (Фаза 10, И2 — Баг-Т): игрок ответил / вопрос закрыт
   * (reveal, question_closed) — таймер замирает на текущем значении, получает
   * класс `q-timer--paused`, тик-звук и подсветка «--low» не срабатывают.
   */
  frozen?: boolean;
}

export function QuestionView({
  question,
  variant = 'answer',
  onAnswer,
  onPos,
  positions = null,
  positionsT = null,
  runDead = null,
  playerNick,
  myAnswer = null,
  reveal = null,
  counts = null,
  liveWords = null,
  participants,
  frozen = false,
}: QuestionViewProps): JSX.Element {
  const { t } = useTranslation();
  const left = useCountdown(question.startedAt, question.durationMs, frozen);
  const prevLeft = useRef(left);
  const kind = questionKind(question);

  // Устойчивость к истечению/невалидности подписи картинки (Фаза 6, И2): подписанный
  // URL может протухнуть посреди сессии или отдать 403 — тогда onError убирает
  // сломанный <img>, не роняя остальной UI (вопрос/варианты остаются). Сбрасывается
  // при смене image (новый вопрос / reconnect со свежей подписью).
  const [imageBroken, setImageBroken] = useState(false);
  useEffect(() => {
    setImageBroken(false);
  }, [question.image]);

  // Тик на последних 5 секундах (И7, Часть 4). playTick — no-op вне браузера/при mute,
  // поэтому безопасен в тестах (jsdom без AudioContext). Пропускаем первый рендер.
  // При frozen (И2, Баг-Т) звук не срабатывает — отсчёт заморожен.
  useEffect(() => {
    if (!frozen && left !== prevLeft.current && left >= 1 && left <= 5) {
      playTick();
    }
    prevLeft.current = left;
  }, [left, frozen]);

  const bodyProps: QuestionBodyProps = {
    question,
    variant,
    myAnswer,
    reveal,
    counts,
    liveWords,
    participants,
    onAnswer: (answer: PlayerAnswer) => onAnswer?.(answer),
    onPos,
    positions,
    positionsT,
    runDead,
    playerNick,
  };

  return (
    <div className="q-view" role="group" aria-label={t('question.viewAria')}>
      <div className="q-meta">
        <span className="q-progress">
          {t('question.progress', { index: question.index + 1, total: question.total })}
        </span>
        <span
          className="q-kind"
          aria-label={t('question.kindAria', {
            // Незнакомый kind — нейтральная подпись (§4): t('kind.…') по
            // несуществующему ключу дал бы сырой ключ в UI.
            kind: kind === 'unknown' ? t('unknown.kindBadge') : t(`kind.${kind}`),
          })}
        >
          {kind === 'unknown' ? t('unknown.kindBadge') : t(`kind.${kind}`)}
        </span>
        <span
          className={
            frozen ? 'q-timer q-timer--paused' : `q-timer${left <= 5 ? ' q-timer--low' : ''}`
          }
          aria-live="polite"
          data-testid="question-timer"
        >
          {left}
        </span>
      </div>

      {question.image && !imageBroken ? (
        <img
          className="q-image"
          loading="lazy"
          decoding="async"
          src={question.image}
          alt={question.title}
          onError={() => setImageBroken(true)}
        />
      ) : null}

      <h2 className="q-title">
        <FormulaText text={question.title} />
      </h2>

      {/* Фаза 16 (B3): блок кода вопроса — между заголовком и телом (под
          текстом/картинкой, над вариантами); только когда у вопроса есть code.
          Покрывает игрока и хоста (общий QuestionView); подсветка — CodeBlock. */}
      {question.code ? <CodeBlock code={question.code} lang={question.codeLang} /> : null}

      {renderBody(kind, question.questionId, bodyProps)}
    </div>
  );
}

function renderBody(
  kind: ReturnType<typeof questionKind>,
  key: number,
  props: QuestionBodyProps,
): JSX.Element {
  switch (kind) {
    case 'poll':
      return <PollView key={key} {...props} />;
    case 'open':
      return <OpenView key={key} {...props} />;
    case 'match':
      return <MatchView key={key} {...props} />;
    case 'order':
      return <OrderView key={key} {...props} />;
    case 'word':
      return <WordView key={key} {...props} />;
    case 'multi':
      return <MultiView key={key} {...props} />;
    case 'run':
      // Первый тип с разными answer/spectate-компонентами (§1.3 Р12): игрок
      // играет (RunView + client-authoritative физика + pos ~3 Гц), ведущий
      // spectate-ит всех игроков (RunHostView, positions-поток).
      return props.variant === 'answer' ? (
        <RunView key={key} {...props} />
      ) : (
        <RunHostView key={key} {...props} />
      );
    case 'choice':
      return <ChoiceView key={key} {...props} />;
    default:
      // Незнакомый kind (будущие типы, §4 спеки Фазы 14): read-only заглушка
      // вместо пустого ChoiceView; клиент ничего не шлёт серверу.
      return <UnknownKindView key={key} {...props} />;
  }
}
